import { Injectable, inject } from '@angular/core';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { OdooMasterService } from '@app-core/odoo/odoo-master.service';
import { UserService } from '@app-core/user/user.service';
@Injectable({ providedIn: 'root' })
export class AuthService extends OdooMasterService {
	private _userService = inject(UserService);

	// -----------------------------------------------------------------------------------------------------
	// @ Auth methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * signIn
	 *
	 * Permite realizar login con con email y contraseña
	 * @param credentials Credenciales de acceso del login
	 * @returns Información del usuario que realizó el login
	 */
	signIn(credentials: { user: string; password: string; method: string }): Observable<any> {
		if (this._authenticated) {
			return throwError(() => new Error('El usuario ya está autenticado.'));
		}
		return this.sendRequest('/api/web/user/login', credentials).pipe(
			switchMap((response: any) => {
				if (response?.status) {
					// Almacenar los tokens
					this.accessToken = response?.data?.access_token;
					this.refreshToken = response?.data?.refresh_token;
					this.partnerApiKey = response?.data?.partner_api_key;
					// Marcar como autenticado
					this._authenticated = true;
					// Guardar el usuario en el servicio correspondiente
					this._userService.user = response?.data?.user;
				}
				return of(response);
			})
		);
	}
	/**
	 * signIn2FA
	 *
	 * Permite realizar login con email, contraseña y un código de verificación OTP
	 * @param credentials Credenciales de acceso del login
	 * @returns Información del usuario que realizó el login
	 */
	signIn2FA(credentials: { user: string; password: string; code:string; method: string }): Observable<any> {
		if (this._authenticated) {
			return throwError(() => new Error('El usuario ya está autenticado.'));
		}
		return this.sendRequest('/api/web/user/login/2fa', credentials).pipe(
			switchMap((response: any) => {
				if (response?.status) {
					// Almacenar los tokens
					this.accessToken = response?.data?.access_token;
					this.refreshToken = response?.data?.refresh_token;
					this.partnerApiKey = response?.data?.partner_api_key;
					// Marcar como autenticado
					this._authenticated = true;
					// Guardar el usuario en el servicio correspondiente
					this._userService.user = response?.data?.user;
				}
				return of(response);
			})
		);
	}
	/**
	 * signOut
	 *
	 * Cierra la sesión del usuario autenticado
	 */
	signOut(): Observable<any> {
		return this.sendRequest('/api/web/user/logout').pipe(
			switchMap((response: any) => {
				if (response?.status) {
					// Marcar como no autenticado
					this._authenticated = false;
					// Limpiar el usuario
					this._userService.user = {};
					// Eliminar el local storage
					localStorage.removeItem('accessToken');
					localStorage.removeItem('refreshToken');
					localStorage.removeItem('partnerApiKey');
					localStorage.removeItem('user');
				}
				return of(response);
			})
		);
	}

	/**
	 * forceSignOut
	 *
	 * Cierra la sesión del usuario sin importar una respuesta del backend
	 */
	forceSignOut(): Observable<any> {
		// Marcar como no autenticado
		this._authenticated = false;
		// Limpiar el usuario
		this._userService.user = {};
		// Eliminar el local storage
		localStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');
		localStorage.removeItem('partnerApiKey');
		localStorage.removeItem('user');
		return of(true);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Recover Password methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * recoverPasswordFirstStep
	 *
	 * @param email
	 */
	recoverPasswordFirstStep(params: { email: string }): Observable<any> {
		return this.sendRequest('/api/web/user/recover/password/step-one',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	/**
	 * recoverPasswordSecondStep
	 *
	 * @param email,code,password,password_confirm
	 */
	recoverPasswordSecondStep(params: { email: string, code: string, password: string, password_confirm: string }): Observable<any> {
		return this.sendRequest('/api/web/user/recover/password/step-two',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Change Password methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * changePasswordFirstStep
	 *
	 * @param user, current_password
	 */
	changePasswordFirstStep(params: { user: string, current_password: string}): Observable<any>{
		return this.sendRequest('/api/web/user/change/password/step-one',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}
	/**
	 * changePasswordSecondStep
	 *
	 * @param user, current_password, code, password, password_confirm
	 */
	changePasswordSecondStep(params: { user: string, current_password: string, code: string, password: string, password_confirm: string }): Observable<any>{
		return this.sendRequest('/api/web/user/change/password/step-two',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	/*
	 * Cambios de contraseña paso 1
	*/
	changePasswordFirstStepWithUser(params: {user: string, current_password: string}): Observable<any>{
		return this.sendRequest('/api/web/user/change/password/step-one',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	changePasswordFirstStepWithoutUser(params: {user: string, current_password: string}): Observable<any>{
		return this.sendRequest('/api/web/user/change-password/step-one',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	/*
	 * Cambios de contraseña paso 2
	*/
	changePasswordSecondStepWithUser(params: {user: string, code: string, password: string, password_confirm: string}): Observable<any>{
		return this.sendRequest('/api/web/user/change/password/step-two',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	changePasswordSecondStepWithoutUser(params: {user: string, current_password: string, code: string, password: string, password_confirm: string}): Observable<any>{
		return this.sendRequest('/api/web/user/change-password/step-two',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Verification Code methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * sendVerificationCode
	 *
	 * @param user, type
	 */
	sendVerificationCode(params: { user: string, type: '2fa' | 'change_password' }): Observable<any> {
		return this.sendRequest('/api/web/user/get/verification/code',params).pipe(
			switchMap((response: any) => {
				return of(response);
			})
		);
	}
}
